/**
 * API 서비스 (axios 인스턴스)
 * 토큰 자동 갱신 포함
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE_PATH = (() => {
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(API_BASE_URL, origin).pathname.replace(/\/+$/, '');
  } catch {
    return String(API_BASE_URL || '').replace(/\/+$/, '');
  }
})();

function normalizeApiPath(url) {
  if (typeof url !== 'string' || /^https?:\/\//i.test(url)) {
    return url;
  }

  if (API_BASE_PATH.endsWith('/api')) {
    if (url === '/api') return '/';
    if (url.startsWith('/api/')) return url.slice(4);
  }

  return url;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15초 타임아웃 (API 호출이 블로킹되는 것 방지)
  headers: {
    'Content-Type': 'application/json',
  },
});

// 토큰 갱신 중복 방지
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

function onRefreshFailed(error) {
  refreshSubscribers.forEach((callback) => callback(null, error));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

// 안전한 localStorage 접근 (카카오톡 인앱 브라우저 등에서 제한될 수 있음)
export function safeGetItem(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
export function safeSetItem(key, value) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
export function safeRemoveItem(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

// 요청 인터셉터: 토큰 추가
api.interceptors.request.use((config) => {
  config.url = normalizeApiPath(config.url);
  const token = safeGetItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 응답 인터셉터: 401 → 리프레시 시도 → 실패 시 로그아웃
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401이 아니면 그냥 reject
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // 이미 리프레시 시도한 요청이면 무한 루프 방지
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // 인증 관련 엔드포인트는 리프레시 하지 않음
    const authPaths = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/verify-email'];
    if (authPaths.some((p) => originalRequest.url?.includes(p))) {
      return Promise.reject(error);
    }

    const refreshToken = safeGetItem('refresh_token');
    if (!refreshToken) {
      // 리프레시 토큰 없으면 조용히 실패 (로그인 안 된 상태에서의 401은 정상)
      // auth:logout 이벤트는 실제 로그인된 상태에서만 발행
      const hadToken = safeGetItem('auth_token');
      safeRemoveItem('auth_token');
      safeRemoveItem('refresh_token');
      if (hadToken) {
        window.dispatchEvent(new Event('auth:logout'));
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // 이미 갱신 중이면 대기열에 추가
      return new Promise((resolve, reject) => {
        addRefreshSubscriber((newToken, refreshError) => {
          if (refreshError || !newToken) {
            return reject(refreshError || error);
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(originalRequest));
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
        timeout: 10000, // 10초 타임아웃
      });

      const newToken = refreshResponse.data.token;
      const newRefreshToken = refreshResponse.data.refresh_token;

      safeSetItem('auth_token', newToken);
      if (newRefreshToken) {
        safeSetItem('refresh_token', newRefreshToken);
      }

      isRefreshing = false;
      onRefreshed(newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      onRefreshFailed(refreshError);
      safeRemoveItem('auth_token');
      safeRemoveItem('refresh_token');
      window.dispatchEvent(new Event('auth:logout'));
      return Promise.reject(refreshError);
    }
  }
);

// ════════════════════════════════════════════
//  인증 API
// ════════════════════════════════════════════

export const authAPI = {
  // 회원가입
  register: (email, password, name, nickname) =>
    api.post('/auth/register', { email, password, name, nickname }),

  // 로그인
  login: (email, password) =>
    api.post('/auth/login', { email, password }),

  // 현재 사용자 정보
  getMe: () =>
    api.get('/auth/me'),

  // 토큰 갱신
  refreshToken: () =>
    api.post('/auth/refresh'),

  // 이메일 인증
  verifyEmail: (token) =>
    api.get(`/auth/verify-email?token=${token}`),

  // 이메일 인증 메일 재발송
  resendVerification: (email) =>
    api.post('/auth/resend-verification', { email }),

  // 비밀번호 변경
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    }),

  // 비밀번호 찾기
  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email }),

  // 비밀번호 재설정
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, newPassword }),

  // 프로필 수정
  updateProfile: (nickname, name) =>
    api.post('/auth/update-profile', { nickname, name }),

  // 닉네임 중복 확인
  checkNickname: (nickname) =>
    api.post('/auth/check-nickname', { nickname }),

  // 회원 탈퇴
  deleteAccount: () =>
    api.delete('/auth/delete-account'),

  // 내 활동 내역
  getMyActivity: () =>
    api.get('/auth/my-activity'),
};

// ════════════════════════════════════════════
//  OAuth 로그인 링크 생성
// ════════════════════════════════════════════

export const getGoogleLoginUrl = () => {
  const returnHost = window.location.hostname;
  const returnProtocol = window.location.protocol.replace(':', '');
  return `${API_BASE_URL}/auth/google?returnHost=${returnHost}&returnProtocol=${returnProtocol}`;
};

export const getKakaoLoginUrl = () => {
  const returnHost = window.location.hostname;
  const returnProtocol = window.location.protocol.replace(':', '');
  return `${API_BASE_URL}/auth/kakao?returnHost=${returnHost}&returnProtocol=${returnProtocol}`;
};

// ════════════════════════════════════════════
//  구조/입양 기능 허브 API
// ════════════════════════════════════════════

export const rescueAPI = {
  getFeatureCatalog: () => api.get('/rescue/feature-catalog'),

  similarityMatch: (payload) => api.post('/rescue/ai/similarity-match', payload),
  breedAnalysis: (payload) => api.post('/rescue/ai/breed-analysis', payload),
  adoptionCopy: (payload) => api.post('/rescue/ai/adoption-copy', payload),
  photoEnhancePlan: (payload) => api.post('/rescue/ai/photo-enhance-plan', payload),
  nameGenerator: (payload) => api.post('/rescue/ai/name-generator', payload),
  emotionTranslate: (payload) => api.post('/rescue/ai/emotion-translate', payload),
  emergencyResponse: (payload) => api.post('/rescue/ai/emergency-response', payload),
  adoptionReadiness: (payload) => api.post('/rescue/ai/adoption-readiness', payload),

  getMapFeed: () => api.get('/rescue/community/map-feed'),
  getMapStats: () => api.get('/rescue/community/map-stats'),
  fosterMatch: (payload) => api.post('/rescue/community/foster-match', payload),
  getBeforeAfter: () => api.get('/rescue/community/before-after'),

  getAdminOverview: () => api.get('/rescue/erp/admin-overview'),
  getPartnerOverview: () => api.get('/rescue/erp/partner-overview'),

  // Admin ERP
  getAdminUsers: (params) => api.get('/rescue/erp/admin/users', { params }),
  deleteAdminUser: (userId) => api.delete(`/rescue/erp/admin/users/${userId}`),
  getAdminPosts: (params) => api.get('/rescue/erp/admin/posts', { params }),
  deleteAdminPost: (postId) => api.delete(`/rescue/erp/admin/posts/${postId}`),
  getAdminSightings: (params) => api.get('/rescue/erp/admin/sightings', { params }),
  getAdminAnalytics: () => api.get('/rescue/erp/admin/analytics'),
  triggerCrawl: () => api.post('/rescue/erp/admin/crawl'),

  // DB animals (크롤링 저장 데이터)
  getDbAnimals: (params) => api.get('/rescue/db/animals', { params }),
  getDbAnimalsStats: () => api.get('/rescue/db/animals/stats'),

  // Public data
  getPublicAnimals: (params) => api.get('/rescue/public/animals', { params }),
  getPublicShelters: (params) => api.get('/rescue/public/shelters', { params }),
  getPublicSido: () => api.get('/rescue/public/sido'),
  getPublicNews: (params) => api.get('/rescue/public/news', { params }),
};

export const communityAPI = {
  listPosts: (params) => api.get('/community/posts', { params }),
  getPost: (postId) => api.get(`/community/posts/${postId}`),
  createPost: (payload) => api.post('/community/posts', payload),
  updatePost: (postId, payload) => api.put(`/community/posts/${postId}`, payload),
  deletePost: (postId) => api.delete(`/community/posts/${postId}`),

  listComments: (postId) => api.get(`/community/posts/${postId}/comments`),
  createComment: (postId, payload) => api.post(`/community/posts/${postId}/comments`, payload),
  updateComment: (postId, commentId, payload) => api.put(`/community/posts/${postId}/comments/${commentId}`, payload),
  deleteComment: (postId, commentId) => api.delete(`/community/posts/${postId}/comments/${commentId}`),
};

// ════════════════════════════════════════════
//  AI 비전 서비스 API
// ════════════════════════════════════════════

export const visionAPI = {
  // 동물 사진 AI 분석 (FormData로 실제 이미지 전송)
  analyzePet: (payload) => {
    if (payload instanceof FormData) {
      return api.post('/vision/analyze-pet', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/vision/analyze-pet', payload);
  },

  // 실종/발견 신고 CRUD
  createReport: (payload) => api.post('/vision/report', payload),
  listReports: (params) => api.get('/vision/reports', { params }),
  getReport: (reportId) => api.get(`/vision/reports/${reportId}`),
  updateReport: (reportId, payload) => api.put(`/vision/reports/${reportId}`, payload),
  deleteReport: (reportId) => api.delete(`/vision/reports/${reportId}`),

  // AI 유사도 매칭 검색 (FormData로 실제 이미지 전송)
  searchMatch: (payload) => {
    if (payload instanceof FormData) {
      return api.post('/vision/search-match', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/vision/search-match', payload);
  },

  // 공공 DB 유사 동물 검색 (사진 → 크롤링 DB 매칭)
  searchPublicAnimals: (formData) => {
    return api.post('/vision/search-public', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },

  // 주변 신고 (지도)
  nearbyReports: (params) => api.get('/vision/nearby', { params }),

  // 관상 매칭 (FormData로 실제 이미지 전송)
  facePetMatch: (payload) => {
    if (payload instanceof FormData) {
      return api.post('/vision/face-pet-match', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/vision/face-pet-match', payload);
  },

  // 내 이력
  myReports: (params) => api.get('/vision/my-reports', { params }),
  myAnalyses: (params) => api.get('/vision/my-analyses', { params }),
  myFaceMatches: (params) => api.get('/vision/my-face-matches', { params }),

  // 통계
  getStats: () => api.get('/vision/stats'),
};

// ════════════════════════════════════════════
//  AI 도구 API
// ════════════════════════════════════════════
export const aiToolsAPI = {
  debateTopic: (payload) => api.post('/ai-tools/debate-topic', payload),
  postHelper: (payload) => api.post('/ai-tools/post-helper', payload),
  debateCoach: (payload) => api.post('/ai-tools/debate-coach', payload),
  aegisScenario: (payload) => api.post('/ai-tools/aegis-scenario', payload, { timeout: 60000 }),
};

// ════════════════════════════════════════════
//  웹 CLI API
// ════════════════════════════════════════════
export const cliAPI = {
  execute: (command) => api.post('/cli/execute', { command }, { timeout: 60000 }),
  autocomplete: (input) => api.post('/cli/autocomplete', { input }),
};

// ════════════════════════════════════════════
//  파일 업로드 API
// ════════════════════════════════════════════
export const uploadAPI = {
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ════════════════════════════════════════════
//  중앙 관제 센터 API
// ════════════════════════════════════════════
export const controlHubAPI = {
  getServers: () => api.get('/control-hub/servers'),
  getServerDetail: (id) => api.get(`/control-hub/server/${id}`),
  getQuickLinks: () => api.get('/control-hub/quick-links'),
};

// ════════════════════════════════════════════
//  통합 작전실 (Command Center) API
// ════════════════════════════════════════════
export const commandCenterAPI = {
  getSummary: () => api.get('/command/summary'),
  getServiceUsers: (key, params) => api.get(`/command/users/${key}`, { params }),
  getDbStatus: () => api.get('/command/db-status'),
  sendEmail: (payload) => api.post('/command/email/send', payload),
  getEmailRecipients: (key) => api.get(`/command/email/recipients/${key}`),
};

// ════════════════════════════════════════════
//  THE BRIDGE — 통합 작전 지휘소 API
// ════════════════════════════════════════════
export const bridgeAPI = {
  getRegistry: () => api.get('/bridge/registry'),
  getSummary: () => api.get('/bridge/summary'),
  getHealth: () => api.get('/bridge/health'),

  // Site Manager (ERP)
  getSiteMetrics: (svc) => api.get(`/bridge/site-metrics/${svc}`),

  // Users
  getUsers: (svc, params) => api.get(`/bridge/users/${svc}`, { params }),
  deleteUser: (svc, uid) => api.delete(`/bridge/users/${svc}/${uid}`),

  // DB
  getDbStatus: () => api.get('/bridge/db-status'),
  queryTable: (svc, table, params) => api.get(`/bridge/tables/${svc}/${table}`, { params }),

  // Analytics
  getAnalytics: (days = 30) => api.get('/bridge/analytics', { params: { days } }),
  getReport: () => api.get('/bridge/analytics/report'),

  // Email (SES)
  sendEmail: (payload) => api.post('/bridge/email/send', payload),
  getRecipients: (svc) => api.get(`/bridge/email/recipients/${svc}`),

  // Inquiries
  getInquiries: (params) => api.get('/bridge/inquiries', { params }),
  replyInquiry: (id, data) => api.post(`/bridge/inquiries/${id}/reply`, data),
  updateInquiryStatus: (id, status) => api.put(`/bridge/inquiries/${id}/status`, { status }),

  // Usage Logs
  getUsageLogs: (page = 1) => api.get('/bridge/usage-logs', { params: { page } }),

  // Export
  exportExcelUrl: () => `${API_BASE_URL}/bridge/export/excel`,
  exportCsvUrl: (svc) => `${API_BASE_URL}/bridge/export/csv/${svc}`,

  // S3 Backup
  getBackupStatus: () => api.get('/bridge/backup/status'),
  triggerBackup: (svc) => api.post('/bridge/backup/trigger', { service_key: svc || null }),
};

// ════════════════════════════════════════════
//  실시간 교통 CCTV 관제 API
// ════════════════════════════════════════════

// 서버 프록시(미국 EC2)는 ITS의 해외 IP 차단으로 실패할 수 있다.
// 그 경우 서버가 내려준 fallback {url, params}로 브라우저(국내)가 직접 호출한다.
async function itsDirectFetch(fb) {
  const qs = new URLSearchParams(fb.params).toString();
  const direct = await fetch(`${fb.url}?${qs}`);
  const data = await direct.json();
  const raw = data?.response?.data;
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const limited = fb.limit ? list.slice(0, fb.limit) : list;
  return limited.map((it) => ({
    name: it.cctvname || '',
    url: it.cctvurl || '',
    lat: parseFloat(it.coordy || 0),
    lng: parseFloat(it.coordx || 0),
    road: it.roadsectionid || it.cctvformat || '',
  }));
}

async function withItsFallback(request, extra = {}) {
  let res = null;
  try {
    res = await request();
  } catch { /* 프록시 자체 실패 — 폴백 불가, 아래 빈 응답 */ }
  if (res?.data?.ok) return res;
  const fb = res?.data?.fallback;
  if (fb?.url && fb?.params) {
    try {
      const cctvs = await itsDirectFetch(fb);
      return { data: { ok: true, count: cctvs.length, cctvs, via: 'direct', ...extra } };
    } catch { /* 직접 호출도 실패 */ }
  }
  return res || { data: { ok: false, cctvs: [], count: 0 } };
}

export const trafficAPI = {
  getRegions: () => api.get('/traffic/regions'),
  getCCTVs: (params) =>
    withItsFallback(() => api.get('/traffic/cctv', { params }), { region: params?.region }),
  analyze: (payload) => api.post('/traffic/analyze', payload),
  weatherTraffic: (params) => api.get('/traffic/weather-traffic', { params, timeout: 30000 }),
  weatherTrafficAnalyze: (payload) => api.post('/traffic/weather-traffic/analyze', payload, { timeout: 45000 }),
};

// ════════════════════════════════════════════
//  과일 배송 출장 관제 API
// ════════════════════════════════════════════
export const dispatchAPI = {
  create: (data) => api.post('/traffic/dispatch/create', data),
  list: (params) => api.get('/traffic/dispatch/list', { params }),
  detail: (id) => api.get(`/traffic/dispatch/${id}`),
  updateStatus: (id, data) => api.put(`/traffic/dispatch/${id}/status`, data),
  analyzeRoute: (id) => api.post(`/traffic/dispatch/${id}/analyze-route`),
  notify: (id, data) => api.post(`/traffic/dispatch/${id}/notify`, data),
  notificationLog: (params) => api.get('/traffic/dispatch/notifications', { params }),
  publicStatus: (code) => api.get(`/traffic/delivery/${code}`),
  provinces: () => api.get('/traffic/dispatch/provinces'),
  nearbyCctv: (params) => withItsFallback(() => api.get('/traffic/dispatch/nearby-cctv', { params })),
  routeCctv: (data) => withItsFallback(() => api.post('/traffic/dispatch/route-cctv', data)),
  analytics: () => api.get('/traffic/dispatch/analytics'),
  report: () => api.post('/traffic/dispatch/report'),
};

// ════════════════════════════════════════════
//  모비딕 프루트 마켓 API
// ════════════════════════════════════════════
export const fruitMarketAPI = {
  // 공개
  getProducts: (params) => api.get('/fruit-market/products', { params }),
  getProduct: (id) => api.get(`/fruit-market/products/${id}`),
  getCategories: () => api.get('/fruit-market/categories'),

  // 농가 데이터
  getSatelliteFarms: (params) => api.get('/fruit-market/satellite/farms', { params }),
  getSatelliteFarm: (id) => api.get(`/fruit-market/satellite/farm/${id}`),
  analyzeSatellite: (payload) => api.post('/fruit-market/satellite/analyze', payload, { timeout: 60000 }),

  // AI 스캐너
  scanFreshness: (payload) => api.post('/fruit-market/scan/freshness', payload, { timeout: 60000 }),
  scanFreshnessUpload: (formData) => api.post('/fruit-market/scan/freshness', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  scanOrigin: (payload) => api.post('/fruit-market/scan/origin-verify', payload, { timeout: 60000 }),
  scanNutrition: (payload) => api.post('/fruit-market/scan/nutrition', payload, { timeout: 60000 }),

  // 장바구니
  getCart: () => api.get('/fruit-market/cart'),
  addToCart: (payload) => api.post('/fruit-market/cart', payload),
  updateCartItem: (id, payload) => api.put(`/fruit-market/cart/${id}`, payload),
  deleteCartItem: (id) => api.delete(`/fruit-market/cart/${id}`),

  // 주문
  createOrder: (payload) => api.post('/fruit-market/orders', payload),
  getOrders: (params) => api.get('/fruit-market/orders', { params }),
  getOrder: (id) => api.get(`/fruit-market/orders/${id}`),
  cancelOrder: (id) => api.post(`/fruit-market/orders/${id}/cancel`),

  // 관리자 ERP
  adminDashboard: () => api.get('/fruit-market/admin/dashboard'),
  adminProducts: (params) => api.get('/fruit-market/admin/products', { params }),
  adminCreateProduct: (payload) => api.post('/fruit-market/admin/products', payload),
  adminUpdateProduct: (id, payload) => api.put(`/fruit-market/admin/products/${id}`, payload),
  adminDeleteProduct: (id) => api.delete(`/fruit-market/admin/products/${id}`),
  adminOrders: (params) => api.get('/fruit-market/admin/orders', { params }),
  adminUpdateOrderStatus: (id, payload) => api.put(`/fruit-market/admin/orders/${id}/status`, payload),
  adminFarms: () => api.get('/fruit-market/admin/farms'),
  adminCreateFarm: (payload) => api.post('/fruit-market/admin/farms', payload),
  adminAnalytics: () => api.get('/fruit-market/admin/analytics'),
  adminSeed: () => api.post('/fruit-market/admin/seed'),

  // 공공데이터 API
  searchEcoCert: (params) => api.get('/fruit-market/public-data/eco-cert', { params }),
  verifyEcoCert: (payload) => api.post('/fruit-market/public-data/eco-cert/verify', payload),
  getWeather: (params) => api.get('/fruit-market/public-data/weather', { params }),
  getWeatherMonthly: (params) => api.get('/fruit-market/public-data/weather/monthly', { params }),
  getWeatherStations: () => api.get('/fruit-market/public-data/weather/stations'),
  getWeatherRange: (params) => api.get('/fruit-market/public-data/weather/range', { params }),
  getFarmWeather: (farmId) => api.get(`/fruit-market/public-data/weather/farm/${farmId}`),
};

// ═══ 농사로 공공API + AI 분석 ═══
export const nongsaroAPI = {
  // 품종 도슨트
  searchVariety: (params) => api.get('/nongsaro/variety/search', { params }),
  getVarietyDocent: (payload) => api.post('/nongsaro/variety/docent', payload, { timeout: 60000 }),
  // 산지소식 / 안심배지
  getFarmNews: (params) => api.get('/nongsaro/farm-news', { params }),
  getTrustBadge: (payload) => api.post('/nongsaro/trust-badge', payload, { timeout: 60000 }),
  // AI 과일 페어링
  getFruitPairing: (payload) => api.post('/nongsaro/pairing', payload, { timeout: 60000 }),
  // 실내식물 / 그린라이프
  getPlants: () => api.get('/nongsaro/plants/recommend'),
  getPlantDetail: (id) => api.get(`/nongsaro/plants/${id}`),
  getGreenLife: (payload) => api.post('/nongsaro/greenlife', payload, { timeout: 60000 }),
  // AI 과일 비서
  askAssistant: (payload) => api.post('/nongsaro/assistant', payload, { timeout: 60000 }),
  // 배송 안전 리포트 (CCTV + 날씨)
  getDeliverySafety: (payload) => api.post('/nongsaro/delivery-safety', payload, { timeout: 60000 }),
  // 산지 날씨
  getFarmWeather: (params) => api.get('/nongsaro/farm-weather', { params }),
  // 데이터 소스 현황
  getDataSources: () => api.get('/nongsaro/data-sources'),
};

// ═══ 농사직설 — AI 농사 도우미 ═══
export const farmingAPI = {
  // 페르소나 목록
  getPersonas: () => api.get('/farming/personas'),
  // 대시보드
  getDashboard: (params) => api.get('/farming/dashboard', { params }),
  // AI 작물 진단 (비전)
  diagnoseCrop: (formData) => api.post('/farming/diagnose', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  // AI 농사 비서 (텍스트)
  askFarmingAI: (payload) => api.post('/farming/ask', payload, { timeout: 60000 }),
  // 채팅 세션 관리
  listChatSessions: () => api.get('/farming/chat/sessions'),
  getChatSession: (sessionId) => api.get(`/farming/chat/sessions/${sessionId}`),
  createChatSession: (payload) => api.post('/farming/chat/sessions', payload),
  deleteChatSession: (sessionId) => api.delete(`/farming/chat/sessions/${sessionId}`),
  // 재배 가이드
  getCropList: (params) => api.get('/farming/crop-list', { params }),
  getCropGuide: (payload) => api.post('/farming/crop-guide', payload, { timeout: 60000 }),
  // KAMIS 농산물 시세
  getMarketPrice: (params) => api.get('/farming/market-price', { params }),
  // 커뮤니티
  listPosts: (params) => api.get('/farming/community/posts', { params }),
  getPost: (postId) => api.get(`/farming/community/posts/${postId}`),
  createPost: (payload) => api.post('/farming/community/posts', payload),
  updatePost: (postId, payload) => api.put(`/farming/community/posts/${postId}`, payload),
  deletePost: (postId) => api.delete(`/farming/community/posts/${postId}`),
  createComment: (postId, payload) => api.post(`/farming/community/posts/${postId}/comments`, payload),
  deleteComment: (postId, commentId) => api.delete(`/farming/community/posts/${postId}/comments/${commentId}`),
  getAIReply: (postId) => api.post(`/farming/community/posts/${postId}/ai-reply`, {}, { timeout: 60000 }),
  // 농업 인텔리전스 센터
  getIntelligence: (params) => api.get('/farming/intelligence', { params }),
  analyzeIntelligence: (payload) => api.post('/farming/intelligence/analyze', payload, { timeout: 60000 }),
  // 절기 달력
  getCalendar: (params) => api.get('/farming/calendar', { params }),
  getCalendarAIPlan: (payload) => api.post('/farming/calendar/ai-plan', payload, { timeout: 60000 }),
  // 병해충/주간농사 상세
  getPestDetail: (pestId) => api.get(`/farming/pest-detail/${pestId}`, { timeout: 60000 }),
  getWeekDetail: (weekId) => api.get(`/farming/week-detail/${weekId}`, { timeout: 60000 }),
  // 가축질병 발생정보 (KAHIS)
  getDiseaseAlerts: () => api.get('/farming/disease-alerts'),
};

// ═══ arXiv — 논문 대시보드 ═══
export const arxivAPI = {
  search: (params) => api.get('/arxiv/search', { params }),
  trending: (params) => api.get('/arxiv/trending', { params, timeout: 30000 }),
  getPaper: (arxivId) => api.get(`/arxiv/paper/${arxivId}`),
  summarize: (payload) => api.post('/arxiv/summarize', payload, { timeout: 60000 }),
  agriPapers: (params) => api.get('/arxiv/agri-papers', { params, timeout: 30000 }),
  categories: () => api.get('/arxiv/categories'),
};

// ═══ 공급망 인텔리전스 & 다이내믹 프라이싱 ═══
export const supplyChainAPI = {
  // 유가 (오피넷)
  fuelAverage: (params) => api.get('/supply-chain/fuel/average', { params }),
  fuelStations: (params) => api.get('/supply-chain/fuel/stations', { params }),
  fuelTrend: (params) => api.get('/supply-chain/fuel/trend', { params }),
  // AI 다이내믹 프라이싱
  pricingAnalyze: (payload) => api.post('/supply-chain/pricing/analyze', payload, { timeout: 120000 }),
  // 콜드체인 & 디지털 트윈
  coldchainSpecs: () => api.get('/supply-chain/coldchain/specs'),
  coldchainSimulate: (payload) => api.post('/supply-chain/coldchain/simulate', payload, { timeout: 120000 }),
  // 물류 라우팅
  routingOptimize: (payload) => api.post('/supply-chain/routing/optimize', payload, { timeout: 120000 }),
  // 대시보드 & 보고서
  dashboard: () => api.get('/supply-chain/dashboard'),
  generateReport: (payload) => api.post('/supply-chain/report', payload, { timeout: 120000 }),
};

// ═══ KAMIS 농산물 유통정보 ═══
export const kamisAPI = {
  daily: (params) => api.get('/kamis/daily', { params }),
  allDaily: (params) => api.get('/kamis/all-daily', { params }),
  item: (params) => api.get('/kamis/item', { params }),
  recentTrend: (params) => api.get('/kamis/recent-trend', { params }),
  monthlyTrend: (params) => api.get('/kamis/monthly-trend', { params }),
  yearlyTrend: (params) => api.get('/kamis/yearly-trend', { params }),
  regional: (params) => api.get('/kamis/regional', { params }),
  dashboard: (params) => api.get('/kamis/dashboard', { params }),
  items: () => api.get('/kamis/items'),
  compareFuel: (params) => api.get('/kamis/compare-fuel', { params }),
};

// ═══ 행동 분석 트래킹 ═══
export const analyticsAPI = {
  track: (data) => api.post('/analytics/track', data).catch(() => {}),
};

// ═══ ERP — 사용자 이용 데이터 영구 수집 ═══
export const erpAPI = {
  // 데이터 수집
  log: (data) => api.post('/erp/log', data).catch(() => {}),
  // 내 기록 (유저용)
  myHistory: (params) => api.get('/erp/my-history', { params }),
  mySummary: (days) => api.get('/erp/my-summary', { params: { days } }),
  // 관리자용
  adminDashboard: (days) => api.get('/erp/admin/dashboard', { params: { days } }),
  adminLogs: (params) => api.get('/erp/admin/logs', { params }),
  adminUsers: (params) => api.get('/erp/admin/users', { params }),
  adminAggregate: (days) => api.post('/erp/admin/aggregate', {}, { params: { days } }),
};

// ═══ 묘냥의 숲 — 캐릭터 슬롯 관리 + WoW 클래식 시스템 ═══
export const forestAPI = {
  // 캐릭터
  listCharacters: () => api.get('/game/forest/characters'),
  createCharacter: (slot, name, charClass, race) =>
    api.post('/game/forest/characters', { slot, name, charClass, race }),
  deleteCharacter: (slot) => api.delete(`/game/forest/characters/${slot}`),
  checkName: (name) => api.get('/game/forest/characters/check', { params: { name } }),
  getCharacterMeta: (slot) => api.get(`/game/forest/characters/${slot}/meta`),
  // 월드
  world: () => api.get('/game/forest/world'),
  // WoW 시스템 카탈로그
  races: () => api.get('/game/forest/races'),
  classes: () => api.get('/game/forest/classes'),
  talents: (specId) => api.get(`/game/forest/talents/${specId}`),
  professions: () => api.get('/game/forest/professions'),
  factions: () => api.get('/game/forest/factions'),
  dungeons: () => api.get('/game/forest/dungeons'),
  mounts: () => api.get('/game/forest/mounts'),
  achievements: () => api.get('/game/forest/achievements'),
  items: () => api.get('/game/forest/items'),
  // 캐릭터 액션
  setSpec: (slot, spec) => api.post(`/game/forest/characters/${slot}/spec`, { spec }),
  spendTalent: (slot, talent) => api.post(`/game/forest/characters/${slot}/talent`, { talent }),
  resetTalents: (slot) => api.post(`/game/forest/characters/${slot}/talent/reset`),
  learnProfession: (slot, profession) =>
    api.post(`/game/forest/characters/${slot}/profession/learn`, { profession }),
  buyMount: (slot, mount) => api.post(`/game/forest/characters/${slot}/mount/buy`, { mount }),
  useMount: (slot, mount) => api.post(`/game/forest/characters/${slot}/mount/use`, { mount }),
  enterDungeon: (slot, dungeon) =>
    api.post(`/game/forest/characters/${slot}/dungeon/enter`, { dungeon }),
  // 카카오톡 별칭 (KakaoTalk MessengerBot R 연동)
  listKakaoAliases: () => api.get('/game/forest/kakao/aliases'),
  createKakaoAlias: (alias, pin, defaultSlot) =>
    api.post('/game/forest/kakao/aliases', { alias, pin, defaultSlot }),
  deleteKakaoAlias: (alias) => api.delete(`/game/forest/kakao/aliases/${encodeURIComponent(alias)}`),
  resetKakaoPin: (alias, pin) =>
    api.post(`/game/forest/kakao/aliases/${encodeURIComponent(alias)}/pin`, { pin }),
  publicRankings: () => api.get('/game/forest/kakao/rankings/public'),
};

export default api;
